import type { PlayerStats } from '../data/EngineData';
import type { EngineMatchEvent } from './MatchEvent';
import type { MatchState } from './MatchState';
import type { PlayerRuntimeState } from './PlayerRuntimeState';

const STAT_KEYS: Array<keyof PlayerStats> = [
  'kick', 'dribble', 'pass', 'defense', 'speed', 'power', 'control', 'stamina',
];

export class FurySystem {
  update(state: MatchState, automaticSides: readonly (0 | 1)[] = [0, 1]): EngineMatchEvent[] {
    const events: EngineMatchEvent[] = [];
    for (const side of [0, 1] as const) {
      const fury = state.fury[side];
      if (fury.active && state.time + 1e-9 >= (fury.endsAt ?? Infinity)) events.push(this.expire(state, side));
    }
    for (const side of automaticSides) {
      if (this.shouldActivate(state, side)) events.push(this.activate(state, side));
    }
    return events;
  }

  activateManually(state: MatchState, side: 0 | 1): EngineMatchEvent | undefined {
    const fury = state.fury[side];
    if (fury.used || fury.active || state.time >= state.config.durationSeconds) return undefined;
    return this.activate(state, side);
  }

  activationUtility(state: MatchState, side: 0 | 1): number {
    const minute = state.time / 60;
    const opponent = side === 0 ? 1 : 0;
    const scoreDifference = state.score[side] - state.score[opponent];
    const timeUrgency = Math.max(0, minute - state.config.furyMinimumActivationMinute) * 1.3;
    const scoreUrgency = scoreDifference < 0 ? 14 + Math.min(18, -scoreDifference * 5) : scoreDifference === 0 ? 6 : 0;
    const emergency = minute >= 50 && scoreDifference <= -2 ? 10 : 0;
    const momentumUrgency = Math.max(0, -state.momentum[side]) * 0.8;
    const responseUrgency = state.fury[opponent].active ? 6 : 0;
    const tactics = state.teams[side].tactics;
    const tacticalUrgency = Math.max(0, (tactics.mentality ?? 0.5) + (tactics.pressing ?? 0.5) - 1) * 5;
    return timeUrgency + scoreUrgency + emergency + momentumUrgency + responseUrgency + tacticalUrgency;
  }

  private shouldActivate(state: MatchState, side: 0 | 1): boolean {
    const fury = state.fury[side];
    if (fury.used || fury.active) return false;
    const minute = state.time / 60;
    if (minute >= state.config.furyForcedActivationMinute) return true;
    const opponent = side === 0 ? 1 : 0;
    const emergency = minute >= 50 && state.score[side] <= state.score[opponent] - 2;
    if (minute < state.config.furyMinimumActivationMinute && !emergency) return false;
    return this.activationUtility(state, side) >= state.config.furyActivationUtilityThreshold;
  }

  private activate(state: MatchState, side: 0 | 1): EngineMatchEvent {
    const fury = state.fury[side];
    fury.used = true;
    fury.active = true;
    fury.activatedAt = state.time;
    fury.endsAt = Math.min(state.config.durationSeconds, state.time + state.config.furyDurationSeconds);
    for (const player of this.teamPlayers(state, side)) this.applyBoost(state, player);
    return this.emit(state, side, 'FURY_STARTED', `${state.teams[side].name} entra en Fase de Furor`);
  }

  private expire(state: MatchState, side: 0 | 1): EngineMatchEvent {
    state.fury[side].active = false;
    for (const player of this.teamPlayers(state, side)) player.stats = { ...player.baseStats };
    return this.emit(state, side, 'FURY_ENDED', `La Fase de Furor de ${state.teams[side].name} llega a su fin`);
  }

  private applyBoost(state: MatchState, player: PlayerRuntimeState): void {
    const boosted = { ...player.baseStats };
    for (const key of STAT_KEYS) {
      boosted[key] = Math.min(state.config.furyMaximumEffectiveStat, player.baseStats[key] * state.config.furyStatMultiplier);
    }
    player.stats = boosted;
  }

  private teamPlayers(state: MatchState, side: 0 | 1): PlayerRuntimeState[] {
    return state.players.filter((player) => player.teamSide === side);
  }

  private emit(state: MatchState, side: 0 | 1, type: 'FURY_STARTED' | 'FURY_ENDED', message: string): EngineMatchEvent {
    const actor = this.teamPlayers(state, side)
      .sort((a, b) => b.baseStats.power + b.baseStats.control - a.baseStats.power - a.baseStats.control
        || a.playerId.localeCompare(b.playerId))[0];
    const event: EngineMatchEvent = {
      sequence: state.events.length,
      second: Number(state.time.toFixed(3)),
      type,
      team: side,
      actorId: actor.playerId,
      actorSourcePlayerId: actor.sourcePlayerId ?? actor.playerId,
      actorAlias: actor.alias,
      position: { ...state.ball.position },
      success: true,
      message,
      homeScore: state.score[0],
      awayScore: state.score[1],
      action: actor.currentAction,
    };
    state.events.push(event);
    state.recentEvents.push(event);
    if (state.recentEvents.length > 32) state.recentEvents.splice(0, state.recentEvents.length - 32);
    return event;
  }
}
